/**
 * ZooCare Rådgivaren, storefront side.
 *
 * Thin on purpose. Every judgement — whether a message is an emergency, what
 * may be said about delivery, which products are allowed out — is made in the
 * control tower, because a rule that lives in a theme asset is a rule anybody
 * can read and nobody can test. This file draws what it is handed.
 *
 * It does two things the server cannot. It renders an emergency differently
 * from an ordinary reply, so that "ring veterinär nu" does not arrive looking
 * like a product recommendation. And when the answer is about an order, it
 * opens the lookup we already built rather than asking the customer to go and
 * find it.
 */
(() => {
  const root = document.querySelector('[data-zc-chat]');
  if (!root) return;

  /**
   * Where the answer comes from. Same origin when the theme is being run
   * against a control tower on this machine, so the widget can be exercised
   * for real before it is pushed; the live host everywhere else.
   */
  const TOWER = location.hostname === 'localhost' ? '' : 'https://control.zoocare.se';
  const API = `${TOWER}/api/public/chat`;
  const SUPPORT_API = `${TOWER}/api/public/support`;

  const openButton = root.querySelector('[data-zc-chat-open]');
  const panel = root.querySelector('[data-zc-chat-panel]');
  const closeButton = root.querySelector('[data-zc-chat-close]');
  const log = root.querySelector('[data-zc-chat-log]');
  /*
   * Where a screen reader is told what happened.
   *
   * The log is role="log", but a bot reply is drawn by swapping the contents
   * of a bubble that already exists — dots first, then the answer. Mutating a
   * node that is already in the tree is announced unreliably, so someone using
   * a screen reader could sit through the whole exchange without being told an
   * answer had arrived. This region gets plain text once, when there is
   * something worth saying.
   */
  const status = document.querySelector('[data-zc-chat-status]');
  const announce = (text) => { if (status) status.textContent = String(text || ''); };
  const form = root.querySelector('[data-zc-chat-form]');
  const field = form.querySelector('textarea');
  const send = form.querySelector('button[type="submit"]');
  const chips = root.querySelector('[data-zc-chat-chips]');

  const humanButton = root.querySelector('[data-zc-chat-human]');
  const humanPanel = root.querySelector('[data-zc-chat-human-panel]');
  const humanClose = root.querySelector('[data-zc-chat-human-close]');
  const humanForm = root.querySelector('[data-zc-chat-human-form]');
  const humanErrors = root.querySelector('[data-zc-chat-human-errors]');

  root.hidden = false;

  const escape = (value) => String(value ?? '').replace(/[&<>"]/g, (c) => (
    { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;' }[c]
  ));

  const money = (value) => `${new Intl.NumberFormat('sv-SE').format(Math.round(value))} kr`;

  /** Paragraphs, not innerHTML of whatever came back. */
  function paragraphs(text) {
    return String(text || '')
      .split(/\n{2,}/)
      .map((part) => `<p>${escape(part.trim())}</p>`)
      .join('');
  }

  function bubble(role, html, kind) {
    const node = document.createElement('div');
    node.className = `zc-chat__msg zc-chat__msg--${role}${kind === 'akut' ? ' zc-chat__msg--akut' : ''}`;
    node.innerHTML = html;
    log.append(node);
    log.scrollTop = log.scrollHeight;
    return node;
  }

  function cards(products = []) {
    if (!products.length) return '';
    const rows = products.slice(0, 3).map((product) => {
      const price = product.price === null ? '' : `<span class="zc-chat__card-price">${money(product.price)}</span>`;
      const was = product.compareAt && product.compareAt > product.price
        ? `<span class="zc-chat__card-was">${money(product.compareAt)}</span>` : '';
      const image = product.image
        ? `<img src="${escape(product.image)}" alt="" loading="lazy" width="54" height="54">`
        : '<img alt="" width="54" height="54">';
      return `<a class="zc-chat__card" href="/products/${escape(product.handle)}">
        ${image}
        <span>
          <span class="zc-chat__card-name">${escape(product.title)}</span>
          <span class="zc-chat__card-meta">${price}${was}${product.delivery ? ` &middot; ${escape(product.delivery)}` : ''}</span>
        </span>
      </a>`;
    }).join('');
    return `<div class="zc-chat__cards">${rows}</div>`;
  }

  /**
   * An order question goes to the page built for it.
   *
   * The chat deliberately does not ask for an order number and email inside
   * the bubble: that lookup already exists, it is tested, and it shows the
   * six-step route per parcel. Re-implementing a worse copy here would be the
   * easy thing and the wrong one.
   */
  function lookupLink() {
    return `<p><a href="/pages/spara-order" style="color:#1b5c20;font-weight:700;">Öppna spårningen</a></p>`;
  }

  let waiting = false;

  const post = async (body) => {
    const response = await fetch(API, {
      method: 'POST',
      headers: { 'content-type': 'application/json' },
      body: JSON.stringify(body),
    });
    return { ok: response.ok, data: await response.json().catch(() => ({})) };
  };

  const render = (data) => paragraphs(data.text) + (data.lookup ? lookupLink() : cards(data.products));

  /**
   * Ask in two passes.
   *
   * The first returns in milliseconds and carries everything already known —
   * an emergency, a policy answer, the matched products. The second waits on
   * the model, which takes about nine seconds and cannot be made to take less.
   *
   * Drawing the first pass immediately is the whole point: the customer reads
   * product cards and the urgent wording while the sentence is still being
   * written, instead of watching a spinner and deciding the shop is broken.
   */
  /**
   * The last few turns, so a follow-up means something.
   *
   * Kept in the page and nowhere else: it dies with the tab, is never written
   * to storage, and the server treats it as untrusted text rather than as
   * fact. Trimmed here as well as on the server — the server is the authority,
   * but there is no reason to put a long conversation on the wire every time.
   */
  const MEMORY_TURNS = 6;
  const memory = [];

  const remember = (role, text) => {
    const trimmed = String(text || '').trim();
    if (!trimmed) return;
    memory.push({ role, text: trimmed.slice(0, 300) });
    while (memory.length > MEMORY_TURNS) memory.shift();
  };

  async function ask(message) {
    if (waiting || !message.trim()) return;
    waiting = true;
    send.disabled = true;
    chips?.remove();

    // Snapshotted before this question joins it, so the server is told what
    // came BEFORE — a history that already contains the current message would
    // make every question look like a follow-up to itself.
    const history = memory.slice();
    remember('kund', message);

    bubble('me', paragraphs(message));
    field.value = '';
    field.style.height = 'auto';

    const dots = '<span class="zc-chat__dots"><span></span><span></span><span></span></span>';
    let placeholder = bubble('bot', dots);
    log.setAttribute('aria-busy', 'true');
    announce('Söker svar…');

    try {
      const fast = await post({ message, phase: 'fast', history });
      if (!fast.ok) {
        const fel = fast.data.error || 'Något gick fel här. Mejla kontakt@zoocare.se så hjälper vi dig.';
        placeholder.innerHTML = paragraphs(fel);
        announce(fel);
        return;
      }

      // Everything already known, drawn now.
      placeholder.innerHTML = render(fast.data);
      remember('radgivare', fast.data.text);
      if (!fast.data.pending) announce(fast.data.text);
      if (fast.data.kind === 'akut') placeholder.classList.add('zc-chat__msg--akut');
      if (!fast.data.pending) return;

      // The sentence is still coming: keep the cards, show that it is being
      // written under them rather than replacing what the customer is reading.
      const pending = document.createElement('div');
      pending.className = 'zc-chat__pending';
      pending.innerHTML = `${dots}<span>skriver…</span>`;
      placeholder.prepend(pending);
      log.scrollTop = log.scrollHeight;

      const full = await post({ message, history });
      pending.remove();
      if (full.ok) {
        placeholder.innerHTML = render(full.data);
        announce(full.data.text);
        // Replaces the fast answer we noted a moment ago, rather than adding to it.
        if (memory.length) memory[memory.length - 1] = { role: 'radgivare', text: String(full.data.text || '').slice(0, 300) };
        if (full.data.kind === 'sparrad') placeholder.classList.add('zc-chat__msg--akut');
      }
    } catch {
      const fel = 'Jag når inte servern just nu. Mejla kontakt@zoocare.se så svarar en människa.';
      placeholder.innerHTML = paragraphs(fel);
      announce(fel);
    } finally {
      log.removeAttribute('aria-busy');
      waiting = false;
      send.disabled = false;
      field.focus();
    }
  }

  function open() {
    // The escape hatch shares the frame, so reopening the launcher must put
    // the chat back rather than show both panels stacked.
    const human = root.querySelector('[data-zc-chat-human-panel]');
    if (human) human.hidden = true;
    panel.hidden = false;
    openButton.setAttribute('aria-expanded', 'true');
    openButton.hidden = true;
    field.focus();
  }

  function close() {
    panel.hidden = true;
    openButton.hidden = false;
    openButton.setAttribute('aria-expanded', 'false');
    openButton.focus();
  }

  openButton.addEventListener('click', open);
  closeButton.addEventListener('click', close);
  document.addEventListener('keydown', (event) => {
    if (event.key === 'Escape' && !panel.hidden) close();
  });

  form.addEventListener('submit', (event) => {
    event.preventDefault();
    ask(field.value);
  });

  // Enter sends, Shift+Enter breaks the line — the habit every chat has taught.
  field.addEventListener('keydown', (event) => {
    if (event.key === 'Enter' && !event.shiftKey) {
      event.preventDefault();
      ask(field.value);
    }
  });

  field.addEventListener('input', () => {
    field.style.height = 'auto';
    field.style.height = `${Math.min(field.scrollHeight, 120)}px`;
  });

  chips?.addEventListener('click', (event) => {
    const chip = event.target.closest('.zc-chat__chip');
    if (chip) ask(chip.textContent.trim());
  });

  /* ── the escape hatch ──────────────────────────────────────────────────── */

  /**
   * Handing the conversation to a person.
   *
   * The reply arrives by email, not here, which is why this form asks for an
   * address and not much else. The conversation so far travels with it: the
   * customer has already explained the problem once and should not have to do
   * it again, and the shop needs to know what the assistant told them so the
   * human answer does not contradict it.
   *
   * Nothing about this path depends on the chat having worked. It is reachable
   * from the footer at any time, including when the server is unreachable and
   * every answer in the log is an apology — which is exactly when somebody
   * most wants a person.
   */
  function showHuman() {
    panel.hidden = true;
    humanPanel.hidden = false;
    openButton.hidden = true;
    openButton.setAttribute('aria-expanded', 'true');

    // The question they already typed, so it is not retyped. Only when the
    // box is empty, so this never overwrites something in progress.
    const messageField = humanForm.querySelector('[name="message"]');
    if (messageField && !messageField.value.trim()) {
      const lastAsked = [...memory].reverse().find((turn) => turn.role === 'kund');
      if (lastAsked) messageField.value = lastAsked.text;
    }

    humanForm.querySelector('[name="name"]')?.focus();
  }

  function hideHuman() {
    humanPanel.hidden = true;
    panel.hidden = false;
    field.focus();
  }

  humanButton?.addEventListener('click', showHuman);
  humanClose?.addEventListener('click', hideHuman);

  const showErrors = (messages) => {
    if (!humanErrors) return;
    const list = [].concat(messages).filter(Boolean);
    if (list.length === 0) { humanErrors.hidden = true; return; }
    humanErrors.innerHTML = list.length === 1
      ? escape(list[0])
      : `<ul>${list.map((m) => `<li>${escape(m)}</li>`).join('')}</ul>`;
    humanErrors.hidden = false;
  };

  let sendingMessage = false;

  humanForm?.addEventListener('submit', async (event) => {
    event.preventDefault();
    if (sendingMessage) return;

    const submit = humanForm.querySelector('button[type="submit"]');
    const data = new FormData(humanForm);

    sendingMessage = true;
    submit.disabled = true;
    submit.textContent = 'Skickar…';
    showErrors([]);

    try {
      const response = await fetch(SUPPORT_API, {
        method: 'POST',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify({
          name: data.get('name'),
          email: data.get('email'),
          orderName: data.get('orderName'),
          message: data.get('message'),
          website: data.get('website'),
          // Untrusted on the server, fenced there, and trimmed here so a long
          // conversation is not put on the wire in full.
          history: memory.slice(-10),
          page: location.pathname,
        }),
      });
      const body = await response.json().catch(() => ({}));

      if (!response.ok) {
        showErrors(body.errors?.length
          ? body.errors
          : 'Meddelandet gick inte fram. Mejla kontakt@zoocare.se så hjälper vi dig.');
        return;
      }

      // Replaces the form rather than clearing it, so nobody sends the same
      // message twice wondering whether the first one went.
      humanPanel.querySelector('.zc-chat__human-form').outerHTML = `
        <div class="zc-chat__human-done">
          <h3>Tack, vi har det!</h3>
          <p>${escape(body.message || 'Vi svarar på din e-post så snart vi kan.')}</p>
          ${body.reference ? `<p>Din referens: <code>${escape(body.reference)}</code></p>` : ''}
        </div>`;
      announce('Meddelandet är skickat.');
    } catch {
      showErrors('Jag når inte servern just nu. Mejla kontakt@zoocare.se så svarar en människa.');
    } finally {
      sendingMessage = false;
      // The form may have been replaced by the thank-you above; only re-enable
      // a button that is still on the page.
      if (humanPanel.contains(submit)) {
        submit.disabled = false;
        submit.textContent = 'Skicka meddelandet';
      }
    }
  });

  // Escape closes whichever of the two is open.
  document.addEventListener('keydown', (event) => {
    if (event.key !== 'Escape' || humanPanel.hidden) return;
    humanPanel.hidden = true;
    openButton.hidden = false;
    openButton.setAttribute('aria-expanded', 'false');
    openButton.focus();
  });
})();
